import {
  Box,
  Circle,
  Flex,
  Link,
  SimpleGrid,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  PortalBusServicingCyclesAllQueryGQLType,
  usePortalBusServicingCyclesAllQuery,
} from '~/backup/shared/deals.gql.generated';
import { formatDollars, formatPeriodShort } from '~/backup/shared/format';

type Cycle = PortalBusServicingCyclesAllQueryGQLType['portalBusServicingCycles'][number];

/** One row of the portfolio table: a deal (implementation) + its latest cycle. */
type Deal = {
  implementationId: string;
  dealName: string;
  implementationName: string;
  latest: Cycle;
};

type DealStatus = 'current' | 'warning' | 'overdue';

const STATUS_COLORS: Record<DealStatus, string> = {
  current: colors.green[300],
  warning: colors.orange[300],
  overdue: colors.red[300],
};

/**
 * Collapse every cycle into one row per deal, keeping the latest reporting
 * period (FullDateStr sorts lexicographically, so a string compare works).
 */
function groupDeals(cycles: Cycle[]): Deal[] {
  const byDeal = new Map<string, Cycle>();
  cycles.forEach((c) => {
    const current = byDeal.get(c.implementationId);
    if (!current || c.reportingPeriod > current.reportingPeriod) {
      byDeal.set(c.implementationId, c);
    }
  });
  return Array.from(byDeal.values())
    .map((latest) => ({
      implementationId: latest.implementationId,
      dealName: latest.creditFacilityName,
      implementationName: latest.implementationName,
      latest,
    }))
    .sort((a, b) => a.dealName.localeCompare(b.dealName));
}

/**
 * A deal's health, derived from its latest cycle's reconcile / certificate. A
 * deal is current once the cycle's certificate has been ISSUED — BUS certificates
 * are pre-signed, so issuing one completes the cycle (nothing waits on a
 * signature).
 */
function dealStatus(latest: Cycle): DealStatus {
  if (latest.reconcileStatus === 'fail') return 'overdue';
  // A certificate that failed to generate needs attention, not patience — it is
  // terminal until someone re-runs it, so it reads as overdue rather than pending.
  if (latest.certificateStatus === 'blocked') return 'overdue';
  if (latest.certificateStatus === 'issued') return 'current';
  return 'warning';
}

function SummaryCard({
  label,
  value,
  valueColor,
  subtitle,
}: {
  label: string;
  value: string;
  valueColor?: string;
  subtitle?: string;
}): React.ReactElement {
  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      borderColor={colors.gray[100]}
      p="1.5rem"
    >
      <VStack align="flex-start" spacing="0.25rem">
        <Text
          color={colors.gray[500]}
          fontSize="0.75rem"
          fontWeight={600}
          letterSpacing="0.02em"
        >
          {label}
        </Text>
        <Text fontWeight={600} fontSize="1.75rem" color={valueColor}>
          {value}
        </Text>
        {subtitle && (
          <Text color={colors.gray[500]} fontSize="0.8rem">
            {subtitle}
          </Text>
        )}
      </VStack>
    </Box>
  );
}

export const PortfolioView = (): React.ReactElement => {
  const { data, loading, error } = usePortalBusServicingCyclesAllQuery({
    fetchPolicy: 'cache-and-network',
  });

  if (loading && !data) {
    return (
      <Flex justify="center" align="center" minH="20rem">
        <Spinner />
      </Flex>
    );
  }

  const deals = groupDeals(data?.portalBusServicingCycles ?? []);

  if (error) {
    return (
      <Flex direction="column" padding="2.5rem" gap="0.5rem">
        <Typography variant="page-title" trim>
          Portfolio Overview
        </Typography>
        <Text color={colors.red[400]}>
          Unable to load portfolio data. Please try again.
        </Text>
      </Flex>
    );
  }

  if (deals.length === 0) {
    return (
      <Flex direction="column" padding="2.5rem" gap="0.5rem">
        <Typography variant="page-title" trim>
          Portfolio Overview
        </Typography>
        <Text color={colors.gray[500]}>
          No backup-servicing deals are available on your account yet.
        </Text>
      </Flex>
    );
  }

  const totalPrincipal = deals.reduce(
    (sum, d) => sum + (d.latest.tapeTotalPrincipal ?? 0),
    0,
  );
  const certsPending = deals.filter(
    (d) => d.latest.certificateStatus !== 'issued',
  ).length;

  return (
    <Flex
      direction="column"
      padding={['1.5rem', '1.5rem', '1.5rem', '2.5rem']}
      gap="2rem"
    >
      <Typography variant="page-title" trim>
        Portfolio Overview
      </Typography>

      {/* Summary cards */}
      <SimpleGrid columns={{ base: 1, sm: 3 }} gap="1rem">
        <SummaryCard
          label="TOTAL CURRENT PRINCIPAL"
          value={formatDollars(totalPrincipal)}
        />
        <SummaryCard label="ACTIVE DEALS" value={String(deals.length)} />
        <SummaryCard
          label="CERTIFICATES PENDING"
          value={String(certsPending)}
          valueColor={certsPending > 0 ? colors.orange[400] : undefined}
        />
      </SimpleGrid>

      {/* Deals table */}
      <TableContainer
        borderWidth="1px"
        borderColor={colors.gray[100]}
        borderRadius="md"
      >
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th width="28px" />
              <Th>Deal</Th>
              <Th isNumeric>Current Principal</Th>
              <Th isNumeric>Loans</Th>
              <Th>Latest Cycle</Th>
              <Th>Certificate</Th>
            </Tr>
          </Thead>
          <Tbody>
            {deals.map((d) => (
              <Tr key={d.implementationId}>
                <Td>
                  <Circle
                    size="10px"
                    bg={STATUS_COLORS[dealStatus(d.latest)]}
                  />
                </Td>
                <Td>
                  <Link
                    as={RouterLink}
                    to={`/deals/${d.implementationId}`}
                    fontWeight={500}
                    color={colors.blue[400]}
                  >
                    {d.dealName}
                  </Link>
                  <Text fontSize="0.75rem" color={colors.gray[500]}>
                    {d.implementationName}
                  </Text>
                </Td>
                <Td isNumeric>{formatDollars(d.latest.tapeTotalPrincipal)}</Td>
                <Td isNumeric>
                  {(d.latest.tapeRowCount ?? 0).toLocaleString()}
                </Td>
                <Td>
                  <Text fontSize="0.8rem" fontWeight={600}>
                    {formatPeriodShort(d.latest.reportingPeriod)}
                  </Text>
                  <Text fontSize="0.75rem" color={colors.gray[500]}>
                    {d.latest.cycleStatus}
                  </Text>
                </Td>
                <Td>
                  <Text fontSize="0.8rem">
                    {d.latest.certificateStatus
                      ? d.latest.certificateStatus.toUpperCase()
                      : '—'}
                  </Text>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Flex>
  );
};
